import React, { useState } from "react";
import { useForm } from "react-hook-form";

const Contact = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // State management for form submission
  const [submitStatus, setSubmitStatus] = useState("idle"); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const onSubmit = async (data) => {
    setSubmitStatus("loading");
    setErrorMessage("");

    try {
      // reCAPTCHA Enterprise Token Generation
      let token = "";
      try {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          token = await new Promise((resolve) => {
            window.grecaptcha.enterprise.ready(async () => {
              const t = await window.grecaptcha.enterprise.execute(
                "6LfJD3EsAAAAAILa8elfwlqDm8icBlLIIFcnIfEN",
                { action: "CONTACT" }
              );
              resolve(t);
            });
          });
        }
      } catch (recaptchaError) {
        console.error("reCAPTCHA Error:", recaptchaError);
        // We continue anyway, but ideally, we should handle this
      }

      // Call Firebase Cloud Function
      const response = await fetch(
        "https://us-central1-activo-digital.cloudfunctions.net/enviarEmail",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: data.name,
            email: data.email,
            subject: data.subject,
            message: data.comment,
            recaptchaToken: token, // Sent to backend for verification
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        // Success - show success message
        setSubmitStatus("success");
        reset(); // Reset form
      } else {
        // Error from server
        setSubmitStatus("error");
        setErrorMessage(
          result.message || "Error al enviar el mensaje. Intente nuevamente."
        );
      }
    } catch (error) {
      // Network or other error
      console.error("Error sending message:", error);
      setSubmitStatus("error");
      setErrorMessage(
        "Error de conexión. Por favor, verifique su internet e intente nuevamente."
      );
    }
  };

  // Success message component
  if (submitStatus === "success") {
    return (
      <div className="success-message" style={successMessageStyle}>
        <div className="success-icon" style={successIconStyle}>
          ✓
        </div>
        <h3 style={successTitleStyle}>¡Mensaje Enviado!</h3>
        <p style={successTextStyle}>
          Gracias por contactarnos. Hemos recibido tu mensaje y nos pondremos
          en contacto contigo pronto.
        </p>
        <button
          className="px-btn px-btn-theme"
          onClick={() => setSubmitStatus("idle")}
          style={resetButtonStyle}
        >
          Enviar otro mensaje
        </button>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group mb-3">
              <input
                type="text"
                className="form-control theme-light"
                placeholder="Nombre completo"
                disabled={submitStatus === "loading"}
                {...register("name", { required: true })}
              />
              {errors.name && errors.name.type === "required" && (
                <span className="invalid-feedback">Nombre es requerido</span>
              )}
            </div>
          </div>
          {/* End .col-6 */}

          <div className="col-md-6">
            <div className="form-group mb-3">
              <input
                type="email"
                className="form-control theme-light"
                placeholder="Email"
                disabled={submitStatus === "loading"}
                {...register(
                  "email",
                  {
                    required: "Email es requerido",
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: "Valores no coinsiden con lo el formato correcto",
                    },
                  },
                  { required: true }
                )}
              />
              {errors.email && (
                <span className="invalid-feedback">{errors.email.message}</span>
              )}
            </div>
          </div>
          {/* End .col-6 */}

          <div className="col-12">
            <div className="form-group mb-3">
              <input
                type="text"
                className="form-control theme-light"
                placeholder="Título"
                disabled={submitStatus === "loading"}
                {...register("subject", { required: true })}
              />
              {errors.subject && (
                <span className="invalid-feedback">Título es requerido.</span>
              )}
            </div>
          </div>
          {/* End .col-12 */}

          <div className="col-12">
            <div className="form-group mb-3">
              <textarea
                rows="4"
                className="form-control theme-light"
                placeholder="Escribe aqui:"
                disabled={submitStatus === "loading"}
                {...register("comment", { required: true })}
              ></textarea>
              {errors.comment && (
                <span className="invalid-feedback">Comentario requerido.</span>
              )}
            </div>
          </div>
          {/* End .col-12 */}

          {/* Error message */}
          {submitStatus === "error" && (
            <div className="col-12">
              <div className="alert alert-danger" role="alert" style={errorAlertStyle}>
                {errorMessage}
              </div>
            </div>
          )}

          <div className="col-12">
            <div className="btn-bar">
              <button
                type="submit"
                className="px-btn px-btn-white"
                disabled={submitStatus === "loading"}
                style={submitStatus === "loading" ? loadingButtonStyle : {}}
              >
                {submitStatus === "loading" ? (
                  <>
                    <span className="spinner" style={spinnerStyle}></span>
                    Enviando...
                  </>
                ) : (
                  "Enviar Mensaje"
                )}
              </button>
              <p className="mt-3" style={{ fontSize: "12px", color: "#666" }}>
                This site is protected by reCAPTCHA and the Google{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{" "}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
              </p>
            </div>
          </div>
          {/* End .col-12 */}
        </div>
      </form>
    </>
  );
};

// Inline styles for success message and loading states
const successMessageStyle = {
  textAlign: "center",
  padding: "60px 30px",
  background: "linear-gradient(135deg, rgba(66, 133, 244, 0.05) 0%, rgba(66, 133, 244, 0.1) 100%)",
  borderRadius: "15px",
  animation: "fadeIn 0.5s ease-in",
  border: "1px solid rgba(66, 133, 244, 0.1)",
};

const successIconStyle = {
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, #4285F4 0%, #34A853 100%)",
  color: "white",
  fontSize: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 20px",
  animation: "scaleIn 0.5s ease-out",
};

const successTitleStyle = {
  color: "#4285F4",
  marginBottom: "15px",
  fontSize: "28px",
  fontWeight: "600",
};

const successTextStyle = {
  color: "rgba(255, 255, 255, 0.8)",
  fontSize: "16px",
  lineHeight: "1.6",
  marginBottom: "30px",
};

const resetButtonStyle = {
  marginTop: "10px",
};

const errorAlertStyle = {
  marginBottom: "20px",
  padding: "15px",
  borderRadius: "8px",
  backgroundColor: "#f8d7da",
  color: "#721c24",
  border: "1px solid #f5c6cb",
};

const loadingButtonStyle = {
  opacity: "0.7",
  cursor: "not-allowed",
};

const spinnerStyle = {
  display: "inline-block",
  width: "14px",
  height: "14px",
  border: "2px solid rgba(255, 255, 255, 0.3)",
  borderTop: "2px solid white",
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
  marginRight: "8px",
  verticalAlign: "middle",
};

export default Contact;
